import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ClusterHint,
  DEFECT_STATUS,
  DEFECT_STATUS_TRANSITIONS,
  type DefectStatus,
  type SeverityLevel,
} from '@testflow/shared';
import { type ClipboardEvent, type DragEvent, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  IdLabel,
  KiGeneratedBadge,
  OutcomeChip,
  TestCaseTitleLine,
} from '../../components/atoms.js';
import { SeverityBadge } from '../../components/board.js';
import { ImageLightbox } from '../../components/image-lightbox.js';
import { SavedHint } from '../../components/saved-hint.js';
import { SearchSelect } from '../../components/search-select.js';
import { defectsApi } from '../../lib/defects-api.js';
import {
  isInvalidTransitionError,
  isReporterPendingError,
  isRetestMissingError,
  mapApiError,
} from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { plansApi } from '../../lib/plans-api.js';
import { toast } from '../../lib/toast.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useSavedFlash } from '../../lib/use-saved-flash.js';
import { AffectedSection } from './affected-section.js';
import { DefectThread } from './defect-thread.js';
import { RetestGuardDialog } from './retest-guard-dialog.js';

// #27 (Finding 19): Screenshots am bestehenden Defect nachruesten. Dieselbe Aufnahme-Mechanik wie
// im Fehler-Dialog des Testers (Paste oder Drop), damit niemand zwei Bedienmuster lernen muss.
function readImages(files: FileList | File[], done: (dataUrls: string[]) => void) {
  const images = [...files].filter((f) => f.type.startsWith('image/'));
  if (images.length === 0) return;
  const results: string[] = [];
  for (const file of images) {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') results.push(reader.result);
      if (results.length === images.length) done(results);
    };
    reader.readAsDataURL(file);
  }
}

function ScreenshotBox({
  screenshots,
  onChange,
}: {
  screenshots: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation();
  const append = (added: string[]) => onChange([...screenshots, ...added]);
  const [zoom, setZoom] = useState<number | null>(null);
  // Stabil halten: der Effekt in ImageLightbox haengt an onClose und ruft dort focus() (#37) —
  // ein Inline-Arrow wuerde bei jedem Render den Fokus erneut ins Overlay reissen.
  const closeZoom = useCallback(() => setZoom(null), []);

  return (
    <div
      onPaste={(e: ClipboardEvent<HTMLDivElement>) =>
        readImages(
          [...e.clipboardData.items].map((i) => i.getAsFile()).filter((f): f is File => f !== null),
          append,
        )
      }
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        readImages(e.dataTransfer.files, append);
      }}
      className="rounded-card border border-dashed border-[var(--border)] p-3 text-center text-xs text-text-muted"
    >
      <p>{t('defect.screenshotAdd')}</p>
      {screenshots.length > 0 && (
        <ul className="mt-2 flex flex-wrap justify-center gap-3">
          {screenshots.map((src, i) => (
            <li key={`shot-${i}-${src.length}`} className="relative">
              <button type="button" onClick={() => setZoom(i)} className="block">
                <img
                  src={src}
                  alt={`${t('defect.screenshot')} ${i + 1}`}
                  className="max-h-48 rounded-card border border-[var(--border)]"
                />
              </button>
              <button
                type="button"
                // Kein stopPropagation noetig: Bild-Button und dieses X sind Geschwister im <li>,
                // der Klick laeuft also gar nicht durch den Bild-Button. Dass das Entfernen die
                // Lightbox nicht oeffnet, haelt stattdessen ein Test fest — der wuerde anschlagen,
                // falls das X spaeter ins Bild wandert.
                onClick={() => onChange(screenshots.filter((_, idx) => idx !== i))}
                aria-label={t('defect.screenshotRemove')}
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-status-fail text-[11px] text-white"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {zoom !== null && (
        <ImageLightbox
          images={screenshots}
          startIndex={zoom}
          label={t('defect.screenshot')}
          onClose={closeZoom}
        />
      )}
    </div>
  );
}

// #37: Der Inhalt kennt die Route nicht mehr — er bekommt die defectId gereicht. Dadurch kann das
// Defect-Board (Finding 29) exakt denselben Inhalt im SidePanel zeigen, statt eine reduzierte
// Zweitansicht zu pflegen, die bei jeder Feldaenderung nachgezogen werden muesste.
export function DefectDetail({ defectId }: { defectId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const { project } = useCurrentProject();
  const projectId = project?.project_id ?? '';
  const [hints, setHints] = useState<ClusterHint[] | null>(null);
  const [cascaded, setCascaded] = useState(false);
  // #87: haelt den Zielstatus des zuletzt abgelehnten Uebergangs fest, solange der
  // Begruendungs-Dialog offen ist — null heisst "kein Dialog". #194: `reason` sagt, welcher
  // Server-Guard ihn ausgeloest hat (Nachtest oder Melder-Bestaetigung) und waehlt den Text.
  const [retestGuard, setRetestGuard] = useState<{
    to: DefectStatus;
    reason: 'retest_missing' | 'reporter_pending';
  } | null>(null);

  const { data: defect } = useQuery({
    queryKey: ['defect', projectId, defectId],
    queryFn: () => defectsApi.get(projectId, defectId),
    enabled: Boolean(projectId && defectId),
  });
  const { data: config } = useQuery({
    queryKey: ['defect-config', projectId],
    queryFn: () => defectsApi.config(projectId),
    enabled: Boolean(projectId),
  });
  const { data: members } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => plansApi.members(projectId),
    enabled: Boolean(projectId),
  });
  // #57: Planliste fuer die Zuordnung. Derselbe Query-Key wie ueberall sonst — meist schon im Cache.
  const { data: plans } = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['defect', projectId, defectId] });
    void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
  };
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof defectsApi.patch>[2]) =>
      defectsApi.patch(projectId, defectId, body),
    onSuccess: invalidate,
  });
  // #96: ruhiges, feldnahes "gespeichert"-Feedback (Muster aus Task 9, scope-table.tsx) — die eine
  // geteilte patch-Mutation oben bedient sechs Felder; saveField haengt der jeweiligen Aufrufstelle
  // die Feldkennung an, damit NUR das gerade gespeicherte Feld seinen Hinweis zeigt.
  const { savedId, flash } = useSavedFlash();
  // #204 T6a: Beschriftung „Titel" per htmlFor, weil der Rahmen kein <label> mehr ist.
  const titleInputId = useId();
  const saveField = (field: string, body: Parameters<typeof defectsApi.patch>[2]) => {
    patch.mutate(body, { onSuccess: () => flash(field) });
  };
  // #87: `comment` bleibt Teil der Variablen (nicht der Rueckgabewert), damit onError bei einem
  // erneuten retest_missing (z. B. Server-Race) denselben Zielstatus fuer einen zweiten
  // Dialog-Anlauf kennt. `errorToast: false`: der Begruendungs-Dialog ist das Fehler-Feedback fuer
  // retest_missing — der globale Toast (query-client.ts) waere hier die doppelte Meldung. Fuer
  // jeden ANDEREN Fehler (z. B. invalid_transition bei einer Race) uebernimmt onError unten
  // manuell genau das, was der globale Handler sonst getan haette.
  const transition = useMutation({
    mutationFn: (v: { to: DefectStatus; comment?: string }) =>
      v.comment
        ? defectsApi.transition(projectId, defectId, v.to, v.comment)
        : defectsApi.transition(projectId, defectId, v.to),
    meta: { successToast: 'feedback.statusChanged', errorToast: false },
    onError: (err, v) => {
      if (isRetestMissingError(err)) {
        setRetestGuard({ to: v.to, reason: 'retest_missing' });
        return;
      }
      // #194: derselbe Dialog, anderer Text — der Melder kann die Behebung noch selbst bestaetigen.
      if (isReporterPendingError(err)) {
        setRetestGuard({ to: v.to, reason: 'reporter_pending' });
        return;
      }
      toast.error(mapApiError(t, err));
      // Follow-up #194: Verlorenes Rennen — Status und Knöpfe sind veraltet, also neu laden.
      if (isInvalidTransitionError(err)) invalidate();
    },
    onSuccess: (res) => {
      setRetestGuard(null);
      setCascaded(res.retest_cascaded);
      invalidate();
    },
  });
  const cluster = useMutation({
    mutationFn: () => defectsApi.clusterHints(projectId, defectId),
    onSuccess: (res) => setHints(res),
  });

  if (!defect) return <div className="text-text-muted">{t('common.loading')}</div>;

  const sevById = new Map<string, SeverityLevel>(
    (config?.severity_levels ?? []).map((s) => [s.severity_id, s]),
  );
  const severity = defect.severity_id ? (sevById.get(defect.severity_id) ?? null) : null;
  const nextStates = DEFECT_STATUS_TRANSITIONS[defect.status];
  // #208: Input des Schritts in der UI-Sprache, einmal gewählt und getrimmt ('' = keiner).
  const failedStepInput = defect.failed_step
    ? pickLang(lang, defect.failed_step.input_de, defect.failed_step.input_en).trim()
    : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <IdLabel id={defect.human_id} />
        <SeverityBadge severity={severity} />
        {defect.is_auto && (
          <span className="rounded-chip border border-[var(--border)] px-1 py-0.5 text-[10px] uppercase text-text-muted">
            {t('defect.auto')}
          </span>
        )}
        {/* #126, #186: die Antwort aus dem Fehler-Dialog, nur als Anzeige — bewusst ohne Bedienelement
            (nicht änderbar: sie steht im Lauf, aus dem der Defect entstand). Dieselben Begriffe wie die
            Schaltflächen im Fehler-Dialog, farbig statt grauer 10-px-Chip. Fehlendes Feld (PATCH- und
            POST-Antworten liefern es nicht) zählt wie null: kein Chip. Seit #192 als Baustein
            `OutcomeChip`, den auch die Nur-Lesen-Seitenleiste des Testers nutzt. */}
        <OutcomeChip stopsChain={defect.stops_chain} />
      </div>

      {/* Lebenszyklus-Stepper (DEF-2) */}
      <div className="flex flex-wrap items-center gap-1 text-[11px] uppercase tracking-wide">
        {DEFECT_STATUS.filter((s) => s !== 'abgelehnt').map((s) => (
          <span
            key={s}
            className={`rounded-chip px-2 py-0.5 ${
              s === defect.status ? 'bg-brand-accent text-white' : 'bg-bg-subtle text-text-muted'
            }`}
          >
            {t(`defect.status.${s}`)}
          </span>
        ))}
        {defect.status === 'abgelehnt' && (
          <span className="rounded-chip bg-brand-accent px-2 py-0.5 text-white">
            {t('defect.status.abgelehnt')}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {nextStates.map((to) => (
          <button
            key={to}
            type="button"
            onClick={() => transition.mutate({ to })}
            disabled={transition.isPending}
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:opacity-60"
          >
            {t('defect.transition.to')}: {t(`defect.status.${to}`)}
          </button>
        ))}
      </div>
      {cascaded && <div className="text-sm text-status-warn">{t('defect.retestCascaded')}</div>}
      {/* #194: Hinweis, solange der Melder die Behebung noch pruefen kann (Schliessen braucht dann
          eine Begruendung). */}
      {defect.awaiting_reporter_name && (
        <div className="text-sm text-status-warn">
          {t('defects.awaitingReporter', { name: defect.awaiting_reporter_name })}
        </div>
      )}

      {/* Edit-Felder (DEF-7) */}
      <div className="grid gap-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 sm:grid-cols-2">
        {/* #204 T6a: <div> statt <label> als Rahmen — der Übernehmen-Knopf darf nicht im <label> liegen,
            sonst löste ein Klick auf die Beschriftung „Titel" den Knopf aus. */}
        <div className="flex flex-col gap-1 text-xs text-text-muted sm:col-span-2">
          <span className="flex items-center gap-2">
            <label htmlFor={titleInputId}>{t('defect.titleField')}</label>
            {/* #204: KI-Titel; nach manuellem Speichern liefert das Neuladen title_ai = false. */}
            {defect.title_ai && <KiGeneratedBadge hint={t('defect.aiTitleHint')} />}
            {defect.title_ai && (
              <button
                type="button"
                onClick={() => saveField('title', { title_ai: false })}
                disabled={patch.isPending}
                title={t('defect.acceptAiTitleHint')}
                className="rounded-btn border border-[var(--border)] px-1.5 py-0.5 text-[11px] text-text-primary hover:border-brand-accent disabled:opacity-60"
              >
                ✓ {t('defect.acceptAiTitle')}
              </button>
            )}
          </span>
          <input
            id={titleInputId}
            // Zugaenglicher Name ohne das KI-Abzeichen.
            aria-label={t('defect.titleField')}
            // #204: Remount bei Titelwechsel auf dem Server (KI-Titel per Refetch) — sonst zeigte das
            // uncontrolled Feld weiter den Auto-Titel, und Betreten+Verlassen schriebe ihn zurück.
            key={defect.title}
            defaultValue={defect.title}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== defect.title) saveField('title', { title: v });
            }}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
          <SavedHint visible={savedId === 'title'} />
        </div>
        {/* #188: Testfall-Titel als eigene Zeile unter dem Titel-Eingabefeld — nur Anzeige, der
            Defect-Titel in der Eingabe bleibt unverändert. */}
        <div className="sm:col-span-2 empty:hidden">
          <TestCaseTitleLine
            titleDe={defect.test_case_title_de}
            titleEn={defect.test_case_title_en}
          />
        </div>
        <label className="flex flex-col gap-1 text-xs text-text-muted sm:col-span-2">
          {/* „Herkunft: Fehler-Lauf" stimmt nur fuer den Auto-Defect. Seit #39 kann ein Defect
              auch von Hand entstehen — dort waere das Label schlicht falsch. */}
          {defect.is_auto ? t('defect.origin') : t('defect.descriptionManual')}
          <textarea
            defaultValue={defect.description ?? ''}
            onBlur={(e) => saveField('description', { description: e.target.value.trim() || null })}
            className="min-h-[64px] rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
          <SavedHint visible={savedId === 'description'} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defect.filter.severity')}
          <select
            value={defect.severity_id ?? ''}
            onChange={(e) => saveField('severity_id', { severity_id: e.target.value || null })}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">{t('defect.severityNone')}</option>
            {(config?.severity_levels ?? []).map((s) => (
              <option key={s.severity_id} value={s.severity_id}>
                {pickLang(lang, s.label_de, s.label_en)}
              </option>
            ))}
          </select>
          <SavedHint visible={savedId === 'severity_id'} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defect.errorType')}
          <select
            value={defect.error_type_id ?? ''}
            onChange={(e) => saveField('error_type_id', { error_type_id: e.target.value || null })}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">{t('defect.severityNone')}</option>
            {(config?.error_types ?? []).map((et) => (
              <option key={et.error_type_id} value={et.error_type_id}>
                {pickLang(lang, et.label_de, et.label_en)}
              </option>
            ))}
          </select>
          <SavedHint visible={savedId === 'error_type_id'} />
        </label>
        <div className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defect.filter.assignee')}
          <SearchSelect
            label={t('defect.filter.assignee')}
            value={defect.assignee_id ?? ''}
            onChange={(v) => saveField('assignee_id', { assignee_id: v || null })}
            options={(members ?? []).map((m) => ({ value: m.user_id, label: m.name }))}
            emptyLabel={t('defect.unassigned')}
            className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
          <SavedHint visible={savedId === 'assignee_id'} />
        </div>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {/* #57: aenderbar, nicht nur angezeigt — sonst waere ein falsch zugeordneter Defect
              nicht korrigierbar und das Feld haette einen Schreiber ohne Leser-Korrektur. */}
          {t('defect.plan')}
          <select
            value={defect.test_plan_id ?? ''}
            onChange={(e) => saveField('test_plan_id', { test_plan_id: e.target.value || null })}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">{t('defect.noPlan')}</option>
            {(plans ?? []).map((p) => (
              <option key={p.test_plan_id} value={p.test_plan_id}>
                {p.name}
              </option>
            ))}
          </select>
          <SavedHint visible={savedId === 'test_plan_id'} />
        </label>
        <div className="text-xs text-text-muted sm:col-span-2">
          {t('defect.linkedTestCase')}:{' '}
          {/* #27 (Finding 17): vom Defect zum Testfall navigieren. Bis hierher stand die ID nur
              als Text da — wer den Testfall lesen wollte, musste ihn im Katalog suchen. */}
          {defect.test_case_id && defect.test_case_human_id ? (
            <Link
              to={`/catalog?test_case=${defect.test_case_id}`}
              title={t('defect.openTestCase')}
              className="underline hover:text-brand-accent"
            >
              <IdLabel id={defect.test_case_human_id} />
            </Link>
          ) : (
            '—'
          )}
          {defect.tcode ? ` · ${defect.tcode}` : ''}
          {/* #208: Schritt-Nummer plus Input (bei BW-Tests der Datenquellen-Name). */}
          {defect.failed_step
            ? ` · ${t('defect.step', { no: defect.failed_step.step_no })}${
                failedStepInput ? ` · ${failedStepInput}` : ''
              }`
            : ''}
        </div>
      </div>

      {/* Screenshots (DEF-1 aus dem Fehler-Lauf; #27/Finding 19: nachtraeglich ergaenzbar) */}
      <ScreenshotBox
        screenshots={defect.screenshots}
        onChange={(next) => patch.mutate({ screenshots: next })}
      />

      {/* #105: „blockiert außerdem" — zusätzliche Testfälle NEBEN dem primären Testfall oben. */}
      <AffectedSection
        projectId={projectId}
        defectId={defectId}
        primaryTestCaseId={defect.test_case_id}
      />

      {/* Cluster-/Dubletten-Hinweise on-demand (DEF-6/AI-F) */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => cluster.mutate()}
          disabled={cluster.isPending}
          className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent disabled:opacity-60"
        >
          {cluster.isPending ? t('defect.cluster.loading') : t('defect.cluster.show')}
        </button>
        {hints !== null && hints.length === 0 && (
          <div className="text-sm text-text-muted">{t('defect.cluster.none')}</div>
        )}
        {hints?.map((h) => (
          <div key={h.defect_id} className="rounded-card border border-[var(--border)] p-2 text-sm">
            <IdLabel id={h.human_id} /> · {t(`defect.cluster.${h.kind}`)} — {h.reason}
          </div>
        ))}
      </div>

      <DefectThread projectId={projectId} defectId={defectId} />

      {retestGuard && (
        <RetestGuardDialog
          pending={transition.isPending}
          reason={retestGuard.reason}
          reporterName={defect.awaiting_reporter_name}
          onCancel={() => setRetestGuard(null)}
          onConfirm={(comment) => transition.mutate({ to: retestGuard.to, comment })}
        />
      )}
    </div>
  );
}

// Route-Huelle. Die Route bleibt bestehen: /defects/:defectId ist Deeplink-Ziel aus den
// Benachrichtigungen — der Pfad kommt seit I1 aus `deeplinkForNotification` (@testflow/shared) und
// wird fuer Empfaenger OHNE `defect.edit` gar nicht erst vergeben. Wer aus einer Mail kommt,
// landet weiter auf der Vollseite — findet dort aber jetzt den Weg zurueck aufs Board.
export function DefectDetailPage() {
  const { t } = useTranslation();
  const { defectId = '' } = useParams();
  return (
    <div className="space-y-3 p-6">
      <Link to="/defects" className="inline-block text-sm text-text-muted hover:text-brand-accent">
        ← {t('defect.backToBoard')}
      </Link>
      <DefectDetail defectId={defectId} />
    </div>
  );
}
